import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { LibraryState } from "@pushback-and-chaos/contracts";
import { LIBRARY } from "../design/copy.js";
import { CardRows, CaseKindRow, CasePaperCover, caseKindWords } from "./case-card.js";
import { Pill } from "./primitives.js";
import { remBase, usePointerDrag } from "./use-drag.js";
import type { LibraryCase } from "../screens/library.js";

/**
 * THE LIGHTBOX (library pass, 2026-09-08; shared with the front door,
 * 2026-09-12): a case expanded beside the card it came from, dragged by its
 * header, with the case's own affordances — walk back in, open, start over,
 * read the prologue, save. ONE AT A TIME: opening a second minimises the
 * first, and `useCaseLightbox` owns exactly that rule so the library and the
 * front door cannot drift.
 *
 * PAPER, like the cards it opens from (2026-09-22): the case paper picked up
 * and laid over the page the way a document window lies over the play
 * surface — the same paper and edge, the document window's heavier shadow
 * because it floats, square corners, the kind row in its bar with "put it
 * down." across from it, the painting in its hairline 4:3 frame, Museo 700
 * for the client, the rows on rules. Nothing it says changed.
 */
export const LIGHTBOX_W_REM = 46;
export const LIGHTBOX_MARGIN_REM = 1;
/** How long a minimising lightbox stays in the tree for its exit animation. */
export const LEAVE_MS = 260;
export const DRAG_Z = 1000;

interface Point {
  readonly x: number;
  readonly y: number;
}

/** What the one open lightbox holds: a case by slug, or the desk's focus, practice (2026-09-23) or email choices (2026-10-07) paper. */
export type LightboxKind = "case" | "focus" | "practice" | "email";

export interface LightboxState {
  readonly kind: LightboxKind;
  /** The case's slug; empty for the focus and practice papers. */
  readonly slug: string;
  /** Viewport px. */
  readonly pos: Point;
  /** A key that changes per opening so the enter animation replays. */
  readonly nonce: number;
}

/** Where an opening lightbox lands: beside the card it came from, wholly inside the viewport. */
export function lightboxPositionFor(rect: DOMRect | null): Point {
  const base = remBase();
  const w = LIGHTBOX_W_REM * base;
  const margin = LIGHTBOX_MARGIN_REM * base;
  const vw = globalThis.innerWidth;
  const vh = globalThis.innerHeight;
  const wantedX = rect === null ? (vw - w) / 2 : rect.left;
  const wantedY = rect === null ? margin * 4 : rect.top - margin * 2;
  return {
    x: Math.round(Math.min(Math.max(wantedX, margin), Math.max(vw - w - margin, margin))),
    y: Math.round(Math.min(Math.max(wantedY, margin), Math.max(vh * 0.5, margin))),
  };
}


/**
 * One lightbox at a time, with its exit animation and Escape handling. The
 * caller renders `lightbox` (entering) and `leaving` (playing out), and
 * decides what each affordance does.
 */
export function useCaseLightbox(): {
  readonly lightbox: LightboxState | null;
  readonly leaving: LightboxState | null;
  readonly confirmingStartOver: boolean;
  readonly setConfirmingStartOver: (next: boolean) => void;
  readonly open: (slug: string, rect: DOMRect | null) => void;
  /** The focus or practice paper picked up (2026-09-23): the same one-at-a-time rule as a case. */
  readonly openKind: (kind: Exclude<LightboxKind, "case">, rect: DOMRect | null, slug?: string) => void;
  readonly close: () => void;
  readonly move: (pos: Point) => void;
} {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [leaving, setLeaving] = useState<LightboxState | null>(null);
  const [confirmingStartOver, setConfirmingStartOver] = useState(false);
  const nonce = useRef(0);

  useEffect(() => {
    if (leaving === null) return;
    const t = setTimeout(() => {
      setLeaving(null);
    }, LEAVE_MS);
    return () => {
      clearTimeout(t);
    };
  }, [leaving]);

  const openAny = useCallback((kind: LightboxKind, slug: string, rect: DOMRect | null) => {
    nonce.current += 1;
    setConfirmingStartOver(false);
    setLightbox((current) => {
      if (current !== null && current.kind === kind && current.slug === slug) return current;
      if (current !== null) setLeaving(current);
      return { kind, slug, pos: lightboxPositionFor(rect), nonce: nonce.current };
    });
  }, []);
  const open = useCallback((slug: string, rect: DOMRect | null) => { openAny("case", slug, rect); }, [openAny]);
  const openKind = useCallback((kind: Exclude<LightboxKind, "case">, rect: DOMRect | null, slug = "") => { openAny(kind, slug, rect); }, [openAny]);

  const close = useCallback(() => {
    setConfirmingStartOver(false);
    setLightbox((current) => {
      if (current !== null) setLeaving(current);
      return null;
    });
  }, []);

  const move = useCallback((pos: Point) => {
    setLightbox((current) => (current === null ? null : { ...current, pos }));
  }, []);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (confirmingStartOver) setConfirmingStartOver(false);
      else close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox, confirmingStartOver, close]);

  return { lightbox, leaving, confirmingStartOver, setConfirmingStartOver, open, openKind, close, move };
}

export function CaseLightbox({
  entry,
  state,
  pos,
  leaving,
  busy,
  saved,
  onMove,
  onClose,
  onContinue,
  onStart,
  onStartOver,
  onReadPrologue,
  onToggleSaved,
}: {
  entry: LibraryCase;
  state: LibraryState | null;
  pos: Point;
  leaving: boolean;
  busy: boolean;
  saved: boolean;
  onMove: (pos: Point) => void;
  onClose: () => void;
  onContinue: (sessionId: string, caseSlug: string) => void;
  onStart: () => void;
  /** The case already has an open run: ask before throwing it away. */
  onStartOver: () => void;
  onReadPrologue: () => void;
  onToggleSaved: () => void;
}): ReactNode {
  const [delta, setDelta] = useState<Point | null>(null);
  const handle = usePointerDrag({
    onMove: (dx, dy) => {
      setDelta({ x: dx, y: dy });
    },
    onEnd: (end) => {
      setDelta(null);
      if (end.moved) onMove({ x: pos.x + end.dx, y: pos.y + end.dy });
    },
  });
  const open = state?.openEngagements.find((e) => e.caseSlug === entry.slug) ?? null;
  const finished = state?.completed.includes(entry.slug) ?? false;
  const base = remBase();
  const width = Math.min(LIGHTBOX_W_REM * base, globalThis.innerWidth - 2 * LIGHTBOX_MARGIN_REM * base);
  const x = Math.min(Math.max(pos.x + (delta?.x ?? 0), 0), Math.max(globalThis.innerWidth - width, 0));
  const y = Math.max(pos.y + (delta?.y ?? 0), 0);

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-label={entry.client}
      aria-hidden={leaving ? "true" : undefined}
      className={`pc-case-lightbox fixed flex max-h-[calc(100svh-2rem)] flex-col overflow-hidden ${leaving ? "pc-lightbox-out" : "pc-lightbox-in"}`}
      style={{ left: `${String(x)}px`, top: `${String(y)}px`, width: `${String(width)}px`, zIndex: leaving ? DRAG_Z + 1 : DRAG_Z + 2 }}
    >
      <header onPointerDown={leaving ? undefined : handle} className="pc-case-lightbox-bar select-none">
        <div className="pc-case-lightbox-kind">
          <CaseKindRow words={caseKindWords(entry)} />
          {entry.industry === null ? null : <span className="pc-case-lightbox-industry">{entry.industry}</span>}
        </div>
        <button type="button" onClick={onClose} aria-label="Put this down">
          {LIBRARY.putDown}
        </button>
      </header>
      <div className="pc-scroll pc-case-lightbox-body">
        <div className="flex flex-col gap-6 sm:flex-row">
          <CasePaperCover slug={entry.slug} className="pc-case-lightbox-cover" />
          <div className="min-w-0 flex-1">
            <h2 className="pc-case-lightbox-title">{entry.client}</h2>
            <p className="pc-case-lightbox-lede">{entry.tension}</p>
            {entry.engagementQuestion !== null && entry.engagementQuestion !== entry.tension ? (
              <p className="pc-case-lightbox-question">{entry.engagementQuestion}</p>
            ) : null}
            <CardRows entry={entry} />
            {finished ? <p className="pc-case-byline">Finished once already. It can be played again.</p> : null}
          </div>
        </div>
        <div className="pc-case-lightbox-doors">
          {open !== null ? (
            <Pill
              onClick={() => {
                onContinue(open.sessionId, open.caseSlug);
              }}
              disabled={busy}
            >
              {LIBRARY.walkBackIn}
            </Pill>
          ) : null}
          <Pill onClick={open !== null ? onStartOver : onStart} disabled={busy}>
            {open !== null ? LIBRARY.startOver : LIBRARY.open}
          </Pill>
          <span className="pc-case-doors" style={{ marginTop: 0 }}>
            <button type="button" onClick={onReadPrologue}>
              {open !== null || finished ? LIBRARY.readPrologueAgain : LIBRARY.readPrologue}
            </button>
            <button type="button" onClick={onToggleSaved}>
              {saved ? LIBRARY.unsave : LIBRARY.save}
            </button>
          </span>
        </div>
      </div>
    </section>
  );
}

/**
 * "Are you sure" before a run is thrown away. A real modal: it takes focus
 * (on the safe choice), Escape and the safe button both keep the run, and
 * only the other button sends `replaceOpen`. The message says plainly what
 * is lost; nothing about it is a nudge either way.
 */
export function StartOverConfirm({ entry, busy, onConfirm, onCancel }: { entry: LibraryCase; busy: boolean; onConfirm: () => void; onCancel: () => void }): ReactNode {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  return (
    <div className="fixed inset-0 flex items-center justify-center p-6" style={{ zIndex: DRAG_Z + 3, background: "color-mix(in srgb, var(--pc-ink) 28%, transparent)" }}>
      <section
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pc-start-over-title"
        aria-describedby="pc-start-over-body"
        className="pc-case-lightbox pc-lightbox-in w-full max-w-[40rem]"
      >
        <div className="pc-case-lightbox-bar" style={{ cursor: "default" }}>
          <div className="pc-case-lightbox-kind">
            <CaseKindRow words={caseKindWords(entry)} />
            <span className="pc-case-lightbox-industry">{entry.client}</span>
          </div>
        </div>
        <div className="pc-case-lightbox-body">
        <h2 id="pc-start-over-title" className="pc-case-lightbox-lede" style={{ marginTop: 0 }}>
          {LIBRARY.startOverTitle}
        </h2>
        <p id="pc-start-over-body" className="pc-case-lightbox-question">
          {LIBRARY.startOverBody}
        </p>
        <div className="pc-case-lightbox-doors">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="inline-flex cursor-pointer items-center rounded-pill border border-ink-soft bg-transparent px-6 py-2 font-sans text-nav text-ink hover:bg-marker"
          >
            {LIBRARY.startOverCancel}
          </button>
          <Pill onClick={onConfirm} disabled={busy}>
            {LIBRARY.startOverConfirm}
          </Pill>
        </div>
        </div>
      </section>
    </div>
  );
}

